import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useMobile from "../../hooks/useMobile";
import { Link, useLocation } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import ConfirmPopUp from "../ConfirmPopUp";
import { INotice } from "../../exports/interfaces";
import { fetchNotices } from "../../api/NoticeApi";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { darkenColor } from "../../exports/functions";
import { useOption } from "../../context/OptionContext";
import TableFooter from "../TableFooter";
import NoticeViewPopUp from "./NoticeViewPopUp";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const NoticeTable = ({
  viewAll,
  notice,
  setNotice,
}: {
  viewAll?: boolean;
  notice: INotice[];
  setNotice: Dispatch<SetStateAction<INotice[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions, categories } = useOption();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<number | string | null>(
    location.state?.selectedId ?? null,
  );

  useEffect(() => {
    if (location.state?.selectedId) {
      setSelectedId(location.state.selectedId);
      window.history.replaceState({ ...window.history.state, usr: null }, "");
    }
  }, [location.state?.selectedId]);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  // Status dropdown & confirm popup state
  const [openStatusId, setOpenStatusId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [statusDropPos, setStatusDropPos] = useState({ top: 0, left: 0 });
  const [showViewPopUp, setShowViewPopUp] = useState(false);
  const [viewNotice, setViewNotice] = useState<INotice | null>(null);

  const noticeStatuses =
    statusOptions?.find((item) => item.name === "Notice Statuses")
      ?.statusOptions || [];

  const filteredNotices = (notice || []).filter((item) =>
    selectedId
      ? item.id === selectedId || item.id === Number(selectedId)
      : true,
  );

  const itemsPerPage = showAll
    ? Math.max(filteredNotices.length, 1)
    : viewAll
      ? 25
      : 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = showAll
    ? filteredNotices
    : filteredNotices.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredNotices.length / itemsPerPage);

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Notice Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const noticeTypeStyle = (type: string) => {
    const noticeTypeOptions =
      categories?.find((item) => item.name === "Notice Type")?.dropdowns || [];

    const matchedType = noticeTypeOptions.find(
      (item) => item.label?.toLowerCase() === type?.toLowerCase(),
    );

    return {
      backgroundColor: matchedType?.color || "#ccc",
      color: darkenColor(matchedType?.color || "#ccc", 40),
    };
  };

  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/notices/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchNotices()
          .then((noticeData) => {
            setNotice(noticeData);
            setShowDelete(false);
            setDeleteId(null);
            sendNotification(`Notice deleted`, user?.user?.userName || "User");
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Notice deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleStatusBadgeClick = (
    id: number,
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = Math.min(noticeStatuses.length * 42 + 12, 260);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const direction = (() => {
      if (spaceBelow >= menuHeight) return "down";
      if (spaceAbove >= menuHeight) return "up";
      return spaceAbove > spaceBelow ? "up" : "down";
    })();

    const top =
      direction === "down"
        ? rect.bottom + window.scrollY
        : rect.top + window.scrollY - menuHeight;

    setStatusDropPos({ top, left: rect.left + window.scrollX });
    setOpenStatusId((prev) => (prev === id ? null : id));
  };

  const handleStatusSelect = (id: number, status: string) => {
    setSelectedStatusId(id);
    setSelectedStatusValue(status);
    setShowConfirm(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedStatusId === null) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("status", selectedStatusValue);

      const response = await axiosClient.put(
        `/notices/${selectedStatusId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.status === 200 || response.status === 201) {
        setNotice((prev) =>
          prev.map((item) =>
            item.id === selectedStatusId
              ? {
                  ...item,
                  status: selectedStatusValue,
                }
              : item,
          ),
        );
        sendNotification(
          `Notice status updated`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Status updated successfully!");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to update status.";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setShowConfirm(false);
        setOpenStatusId(null);
        setSelectedStatusId(null);
        setSelectedStatusValue("");
      }, 1000);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenStatusId(null);
    };
    if (openStatusId !== null) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openStatusId]);

  return (
    <>
      <div className="dashboard-card">
        <div className="card-body">
          <h6 className="mb-3">NOTICE MANAGEMENT</h6>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th className="table-xs">S. no.</th>
                  <th className="table-md">Notice Title</th>
                  <th className="table-sm">Notice Type</th>
                  <th className="table-md">Notice Detail</th>
                  <th className="table-md">Notice Status</th>
                  <th className="table-sm">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((item, index) => (
                    <tr key={index}>
                      <td
                        className="table-xs text-danger"
                        title={String(indexOfFirstItem + index + 1)}
                      >
                        {String(indexOfFirstItem + index + 1).padStart(2, "0")}
                      </td>

                      <td
                        className="table-md table-bold"
                        title={item.noticeTitle}
                      >
                        {item.noticeTitle}
                      </td>

                      <td className="table-sm">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={noticeTypeStyle(item.noticeType)}
                          title={item.noticeType}
                        >
                          {item.noticeType}
                        </span>
                      </td>

                      <td className="table-md" title={item.noticeDetails}>
                        {item.noticeDetails}
                      </td>

                      <td className="table-md">
                        {canEdit("notices") ? (
                          <div
                            className="status-dropdown-container"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span
                              className="badge rounded-pill px-3 py-2 status-badge-clickable mw-100"
                              style={statusStyle(item.status)}
                              onClick={(e) =>
                                handleStatusBadgeClick(item.id, e)
                              }
                              title={item.status}
                            >
                              {item.status}
                            </span>
                          </div>
                        ) : (
                          <span
                            className="badge rounded-pill px-3 py-2 mw-100"
                            style={statusStyle(item.status)}
                            title={item.status}
                          >
                            {item.status}
                          </span>
                        )}
                      </td>

                      <td className="table-sm">
                        <div className="d-flex gap-3 align-items-center">
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                            onClick={() => {
                              setViewNotice(item);
                              setShowViewPopUp(true);
                            }}
                          >
                            <Eye size={30} />
                          </i>
                          <Link
                            to={
                              canEdit("notices")
                                ? `/notices/edit/${item.id}`
                                : "#"
                            }
                            onClick={(e) => {
                              if (!canEdit("notices")) {
                                e.preventDefault();
                              }
                            }}
                            style={{
                              pointerEvents: canEdit("notices")
                                ? "auto"
                                : "none",
                              opacity: canEdit("notices") ? 1 : 0.5,
                            }}
                          >
                            <i
                              data-toggle="tooltip"
                              data-placement="top"
                              title={
                                canEdit("notices") ? "Edit" : "No permission"
                              }
                            >
                              <Pencil size={20} />
                            </i>
                          </Link>
                          <i
                            className={`fa-solid fa-trash text-danger ${
                              canDelete("notices")
                                ? "cursor-pointer"
                                : "opacity-50"
                            }`}
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canDelete("notices") ? "Delete" : "No permission"
                            }
                            style={{
                              pointerEvents: canDelete("notices")
                                ? "auto"
                                : "none",
                            }}
                            onClick={() => {
                              if (!canDelete("notices")) return;
                              setDeleteId(item.id);
                              setShowDelete(true);
                            }}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <TableFooter
          isMobile={isMobile}
          totalItems={filteredNotices.length}
          label="Notices"
          itemsPerPage={viewAll ? 25 : 5}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          showAll={showAll}
          onToggleShowAll={() => {
            setShowAll((prev) => !prev);
            setCurrentPage(1);
          }}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
      {showConfirm && (
        <ConfirmPopUp
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
      <NoticeViewPopUp
        show={showViewPopUp}
        onClose={() => {
          setShowViewPopUp(false);
          setViewNotice(null);
        }}
        notice={viewNotice}
      />
      {openStatusId !== null &&
        createPortal(
          <div
            className={`status-dropdown-menu shadow`}
            style={{
              position: "absolute",
              top: statusDropPos.top,
              left: statusDropPos.left,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {noticeStatuses.map((option) => (
              <button
                key={option.id}
                type="button"
                className="status-dropdown-item"
                onClick={() => {
                  if (openStatusId !== null) {
                    handleStatusSelect(openStatusId, option.label);
                  }
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default NoticeTable;
